import { useCallback, useRef, useState } from 'react';

import { config } from '@/global-config';
import { getAccessToken } from '@/shared/lib/axios';

// SSE event protocol mirrors api/src/features/chat/streaming/sse.handler.ts —
// named events (`conversation_created`, `conversation_titled`, `message_created`,
// `thinking`, `chunk`, `done`, `error`) with JSON `{ type, data }` payloads on
// the `data:` line.

export interface SSEConversationCreated {
  id: string;
  title: string;
}
/** Sent after the second message, when the AI replaces the placeholder title. */
export interface SSEConversationTitled {
  id: string;
  title: string;
}
export interface SSEMessageCreated {
  id: string;
  role: 'user' | 'assistant';
  sequence: number;
  /** Parent message in the conversation tree; `null` for a root user message. */
  parentId: string | null;
}
export interface SSEThinking {
  messageId: string;
  stage?: string;
  detail?: string;
}
export interface SSEChunk {
  content: string;
}
export interface SSEDone {
  messageId: string;
  usage: { inputTokens: number; outputTokens: number; totalTokens: number };
  finishReason: string;
}
export interface SSEError {
  code: string;
  message: string;
}

export interface ChatStreamHandlers {
  onConversationCreated?: (e: SSEConversationCreated) => void;
  onConversationTitled?: (e: SSEConversationTitled) => void;
  onMessageCreated?: (e: SSEMessageCreated) => void;
  onThinking?: (e: SSEThinking) => void;
  onChunk?: (e: SSEChunk) => void;
  onDone?: (e: SSEDone) => void;
  onError?: (e: SSEError) => void;
}

export interface SendStreamParams {
  content: string;
  conversationId?: string;
  gemSlug?: string;
  /** Enable the Shamela library tools for this message. */
  useShamela?: boolean;
  /** Tree parent — the last message on the client's active branch. */
  parentId?: string;
}

export function useChatStream(handlers: ChatStreamHandlers) {
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // The generation target (conversation + assistant message ids) of the
  // in-flight stream — known up front for regenerate/edit, learned from the
  // SSE events on a fresh send. `stop()` needs it to address the server-side
  // generation.
  const streamTargetRef = useRef<{ conversationId: string | null; messageId: string | null }>({
    conversationId: null,
    messageId: null,
  });
  // Stash handlers in a ref so callers don't need to memoize them — re-renders
  // shouldn't tear down an in-flight stream.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  // Local teardown only. Generation is detached server-side: dropping the
  // connection does NOT cancel it — the answer completes and persists, and the
  // thread shows it on the next fetch. Used when switching conversations.
  const abort = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  // Explicit user stop (the ■ button): tells the server to abort the detached
  // generation — it persists whatever partial answer arrived — then tears the
  // local stream down.
  const stop = useCallback(() => {
    const { conversationId, messageId } = streamTargetRef.current;
    if (conversationId && messageId) {
      const accessToken = getAccessToken();
      const headers: Record<string, string> = {};
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
      void fetch(
        `${config.apiBaseUrl}/api/chat/conversations/${conversationId}/messages/${messageId}/stop`,
        { method: 'POST', headers, credentials: 'include' },
      ).catch(() => {});
    }
    abort();
  }, [abort]);

  // Shared SSE driver for both `send` and `regenerate` — same auth headers,
  // abort wiring, error envelopes, and frame parser; only the URL + body differ.
  const runStream = useCallback(
    async (
      url: string,
      body: Record<string, unknown>,
      conversationId?: string,
    ): Promise<void> => {
      abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      streamTargetRef.current = { conversationId: conversationId ?? null, messageId: null };
      setIsStreaming(true);

      try {
        const accessToken = getAccessToken();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        };
        if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

        const res = await fetch(url, {
          method: 'POST',
          headers,
          credentials: 'include',
          signal: ctrl.signal,
          body: JSON.stringify(body),
        });

        if (!res.ok || !res.body) {
          const text = await res.text().catch(() => '');
          const code = res.status === 401 ? 'UNAUTHORIZED' : 'STREAM_ERROR';
          handlersRef.current.onError?.({ code, message: text || res.statusText });
          return;
        }

        // Wrap the live handlers so the stream target is tracked before each
        // frame is dispatched — `stop()` needs the ids mid-stream.
        const trackedHandlers = {
          get current(): ChatStreamHandlers {
            const inner = handlersRef.current;
            const target = streamTargetRef.current;
            return {
              ...inner,
              onConversationCreated: (e: SSEConversationCreated) => {
                target.conversationId = e.id;
                inner.onConversationCreated?.(e);
              },
              onMessageCreated: (e: SSEMessageCreated) => {
                if (e.role === 'assistant') target.messageId = e.id;
                inner.onMessageCreated?.(e);
              },
            };
          },
        };
        await parseEventStream(res.body, ctrl.signal, trackedHandlers);
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') return;
        handlersRef.current.onError?.({
          code: 'NETWORK_ERROR',
          message: (err as Error).message || 'Network error',
        });
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [abort],
  );

  const send = useCallback(
    (params: SendStreamParams): Promise<void> =>
      runStream(`${config.apiBaseUrl}/api/chat/messages/stream`, {
        content: params.content,
        ...(params.conversationId ? { conversationId: params.conversationId } : {}),
        ...(params.gemSlug ? { gemSlug: params.gemSlug } : {}),
        ...(params.parentId ? { parentId: params.parentId } : {}),
        ...(params.useShamela !== undefined ? { useShamela: params.useShamela } : {}),
      }, params.conversationId),
    [runStream],
  );

  // Re-streams an assistant reply. The endpoint emits the same event protocol
  // minus `conversation_created` / the user `message_created`.
  const regenerate = useCallback(
    (params: { conversationId: string; messageId: string; useShamela?: boolean }): Promise<void> =>
      runStream(
        `${config.apiBaseUrl}/api/chat/conversations/${params.conversationId}/messages/${params.messageId}/regenerate`,
        {
          ...(params.useShamela !== undefined ? { useShamela: params.useShamela } : {}),
        },
        params.conversationId,
      ),
    [runStream],
  );

  // Edits a user message — branches the conversation and streams a fresh reply.
  // Emits the full event protocol: `message_created` for the new user message
  // and the assistant, then `thinking`/`chunk`/`done`.
  const editMessage = useCallback(
    (params: { conversationId: string; messageId: string; content: string }): Promise<void> =>
      runStream(
        `${config.apiBaseUrl}/api/chat/conversations/${params.conversationId}/messages/${params.messageId}/edit`,
        { content: params.content },
        params.conversationId,
      ),
    [runStream],
  );

  return { send, regenerate, editMessage, abort, stop, isStreaming };
}

async function parseEventStream(
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal,
  handlersRef: { current: ChatStreamHandlers },
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    if (signal.aborted) {
      try {
        await reader.cancel();
      } catch {
        /* ignore */
      }
      return;
    }
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line (\n\n). Process complete frames
    // and keep the trailing partial frame in the buffer for the next read.
    let separator = buffer.indexOf('\n\n');
    while (separator !== -1) {
      const frame = buffer.slice(0, separator);
      buffer = buffer.slice(separator + 2);
      handleFrame(frame, handlersRef.current);
      separator = buffer.indexOf('\n\n');
    }
  }

  // Flush any trailing frame that didn't end with the blank line.
  if (buffer.trim().length > 0) {
    handleFrame(buffer, handlersRef.current);
  }
}

function handleFrame(frame: string, handlers: ChatStreamHandlers): void {
  // SSE comments (`:` heartbeats) are ignored. Each non-comment line of the form
  // `data: <payload>` carries a `{type, data}` JSON envelope per api/'s handler.
  for (const line of frame.split('\n')) {
    if (!line.startsWith('data:')) continue;
    const raw = line.slice(5).trim();
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw) as { type: string; data: unknown };
      dispatchEvent(parsed.type, parsed.data, handlers);
    } catch {
      // Ignore malformed JSON — keep the stream alive for the next frame.
    }
  }
}

function dispatchEvent(type: string, data: unknown, handlers: ChatStreamHandlers): void {
  switch (type) {
    case 'conversation_created':
      handlers.onConversationCreated?.(data as SSEConversationCreated);
      return;
    case 'conversation_titled':
      handlers.onConversationTitled?.(data as SSEConversationTitled);
      return;
    case 'message_created':
      handlers.onMessageCreated?.(data as SSEMessageCreated);
      return;
    case 'thinking':
      handlers.onThinking?.(data as SSEThinking);
      return;
    case 'chunk':
      handlers.onChunk?.(data as SSEChunk);
      return;
    case 'done':
      handlers.onDone?.(data as SSEDone);
      return;
    case 'error':
      handlers.onError?.(data as SSEError);
      return;
    default:
      return;
  }
}
